import { Skeleton } from "../ui/skeleton";

/**
 * PageSkeleton — placeholder shown while a dashboard page is loading.
 * Mirrors the structure of a page: PageHeader (icon + title + description + actions)
 * + KPI grid + content stripe. All pulses respect dark mode (bg-muted token).
 *
 * Usage:
 *   if (loading) return <PageSkeleton kpis={4} />;
 */
export function PageSkeleton({ kpis = 4, showTabs = true, rows = 5 }) {
  return (
    <div className="space-y-6 animate-fade-in" data-testid="page-skeleton">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>
      </div>

      {/* KPI grid */}
      {kpis > 0 && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${Math.min(kpis, 4)}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: kpis }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-card p-4 space-y-3"
            >
              <Skeleton className="w-9 h-9 rounded-lg" />
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      {showTabs && (
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      )}

      {/* Content rows */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-2/5" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default PageSkeleton;
